import React, { useState, useEffect } from 'react';
import AdminShell from '../components/layout/AdminShell';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import { Badge } from '../components/ui/badge';
import { Printer } from 'lucide-react';
import axios from 'axios';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  stable:      'bg-green-100 text-green-700 border-green-200',
  beta:        'bg-amber-100 text-amber-700 border-amber-200',
  deprecated:  'bg-red-100  text-red-700  border-red-200',
};

export default function VersionHistoryPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [vSort, handleVSort] = useSortState('version', 'desc');
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/system/version`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtDate = d => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: tz }); }
    catch { return d; }
  };

  const handlePrint = () => {
    const currentVersion = data?.version || data?.current?.version || '?';
    const historyData = data?.history || [];
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Eyvo eProcurement - Version History</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1a1a1a; }
          .header { border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { font-size: 28px; font-weight: 700; color: #1a1a1a; }
          .header p { font-size: 14px; color: #6b7280; margin-top: 4px; }
          .meta { display: flex; gap: 20px; margin-top: 12px; font-size: 12px; color: #6b7280; }
          .current-version { background: #eff6ff; border: 2px solid #2563eb; border-radius: 12px; padding: 20px; margin-bottom: 30px; }
          .current-version .label { font-size: 11px; font-weight: 600; color: #2563eb; text-transform: uppercase; letter-spacing: 1px; }
          .current-version .version { font-size: 32px; font-weight: 700; color: #2563eb; margin-top: 4px; }
          .current-version .date { font-size: 12px; color: #6b7280; margin-top: 4px; }
          .release { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 16px; page-break-inside: avoid; }
          .release-header { background: #f9fafb; padding: 12px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
          .release-version { background: #2563eb; color: white; padding: 4px 10px; border-radius: 6px; font-size: 13px; font-weight: 600; font-family: monospace; }
          .release-current { background: #10b981; }
          .release-date { font-size: 12px; color: #6b7280; }
          .release-body { padding: 16px; }
          .release-body ul { list-style: none; }
          .release-body li { font-size: 13px; color: #374151; padding: 6px 0; padding-left: 20px; position: relative; }
          .release-body li:before { content: "✓"; position: absolute; left: 0; color: #10b981; font-weight: bold; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #6b7280; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Eyvo eProcurement</h1>
          <p>Version History & Release Notes</p>
          <div class="meta">
            <span>Current: v${currentVersion}</span>
            <span>Printed: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })}</span>
          </div>
        </div>
        
        <div class="current-version">
          <div class="label">Current Version</div>
          <div class="version">v${currentVersion}</div>
          <div class="date">Deployed: ${data?.deployed_at ? new Date(data.deployed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : '—'}</div>
        </div>
        
        ${historyData.map((v, i) => `
          <div class="release">
            <div class="release-header">
              <span class="release-version ${i === 0 ? 'release-current' : ''}">v${v.version}${i === 0 ? ' (Current)' : ''}</span>
              <span class="release-date">${v.date ? new Date(v.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: tz }) : '—'}</span>
            </div>
            <div class="release-body">
              <ul>
                ${(v.changes || []).map(c => `<li>${c}</li>`).join('')}
              </ul>
            </div>
          </div>
        `).join('')}
        
        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} Eyvo Inc. All rights reserved.</p>
          <p>www.eyvo.com | support@eyvo.com</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <AdminShell>
      <div className="max-w-4xl">
        {/* Header */}
        <div className="pb-5 border-b mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Version History
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Full release history for Eyvo eProcurement.
            </p>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <Printer size={16} />
            Print History
          </button>
        </div>

        {loading ? (
          <div className="py-16"><LoadingSpinner /></div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Could not load version data.</p>
        ) : (
          <>
            {/* Current version hero */}
            <div className="rounded-xl border-2 border-primary bg-primary/5 px-6 py-5 mb-8 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">
                  Current Version
                </p>
                <div className="flex items-center gap-3">
                  <span
                    className="text-3xl font-bold text-primary"
                    style={{ fontFamily: 'Space Grotesk' }}
                  >
                    v{data.current?.version}
                  </span>
                  <span className="text-lg font-semibold">{data.current?.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Released {fmtDate(data.current?.released_at)}
                  {data.current?.released_by && ` by ${data.current.released_by}`}
                </p>
              </div>
              <div className="text-right hidden md:block">
                <span className={`inline-flex items-center border rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[data.current?.status] || STATUS_STYLES.stable}`}>
                  {data.current?.status || 'stable'}
                </span>
              </div>
            </div>

            {/* Full history table */}
            <div className="rounded-xl border bg-card overflow-hidden">
              <table className="w-full" aria-label="Version history">
                <thead>
                  <tr className="bg-muted/50 border-b">
                  <tr className="bg-muted/50 border-b">
                    <SortableTableHeader label="Version" field="version" sortState={vSort} onSort={handleVSort} />
                    <SortableTableHeader label="Release Name" field="label" sortState={vSort} onSort={handleVSort} />
                    <SortableTableHeader label="Date" field="released_at" sortState={vSort} onSort={handleVSort} />
                    <SortableTableHeader label="Status" field="status" sortState={vSort} onSort={handleVSort} />
                    <SortableTableHeader label="Release Notes" field="notes" sortState={vSort} onSort={handleVSort} />
                  </tr>
                  </tr>
                </thead>
                <tbody>
                  {sortData((data.history || []), vSort.field, vSort.dir).map((v, i) => {
                    const isCurrent = v.version === data.current?.version;
                    return (
                      <tr
                        key={v.version}
                        className={`border-b last:border-0 transition-colors ${isCurrent ? 'bg-primary/5' : 'hover:bg-muted/30'}`}
                      >
                        {/* Version badge */}
                        <td className="px-5 py-4 align-top">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-bold font-mono ${
                                isCurrent ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              v{v.version}
                            </span>
                            {isCurrent && (
                              <span className="text-[10px] font-semibold text-primary uppercase tracking-wide">
                                Current
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Release name */}
                        <td className="px-5 py-4 align-top">
                          <p className="text-sm font-semibold">{v.label}</p>
                          {v.released_by && (
                            <p className="text-xs text-muted-foreground mt-0.5">{v.released_by}</p>
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-5 py-4 align-top whitespace-nowrap">
                          <p className="text-sm">{fmtDate(v.released_at)}</p>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4 align-top">
                          <span className={`inline-flex items-center border rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[v.status] || STATUS_STYLES.stable}`}>
                            {v.status || 'stable'}
                          </span>
                        </td>

                        {/* Release notes */}
                        <td className="px-5 py-4 align-top">
                          <p className="text-sm text-muted-foreground leading-relaxed max-w-lg">
                            {v.notes}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-right">
              {data.history?.length} release{data.history?.length !== 1 ? 's' : ''} total
            </p>
          </>
        )}
      </div>
    </AdminShell>
  );
}
